import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAnalyticsV2 } from '@carta/fep-analytics';

// @carta/fep-analytics is a client-side only package. In order to use it with Gatsby,
// we have to load it in a module that's imported lazily in the browser.
const AnalyticsProviderLazy = React.lazy(() => import('./AnalyticsProviderLazy'));

const DEFAULT_DESCRIPTION =
  'Ink is a shared collection of tools, resources and standards that enable teams to build consistent, high-quality product experiences.';
const DEFAULT_IMAGE_URL = 'https://ink.carta.com/og-image.png';

export const AnalyticsProvider = ({ children }) => {
  if (typeof window === 'undefined') return children;

  return (
    <React.Suspense fallback={children || <></>}>
      <AnalyticsProviderLazy>{children}</AnalyticsProviderLazy>
    </React.Suspense>
  );
};

type PageViewProviderProps = {
  children: React.ReactNode;
  pageTitle: string;
  pageDescription?: string;
  pageImageUrl?: string;
};
export const PageViewProvider = ({ children, pageTitle, pageDescription, pageImageUrl }: PageViewProviderProps) => {
  const { trackPageView } = useAnalyticsV2();
  const description = pageDescription || DEFAULT_DESCRIPTION;
  const imageUrl = pageImageUrl || DEFAULT_IMAGE_URL;

  useEffect(() => {
    trackPageView({ title: pageTitle });
  }, [pageTitle, trackPageView]);

  return (
    <>
      <Helmet>
        <title> {`Ink Design System | ${pageTitle}`}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={`Ink Design System | ${pageTitle}`} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Ink Design System | ${pageTitle}`} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />
      </Helmet>
      <AnalyticsProvider>{children}</AnalyticsProvider>
    </>
  );
};
